import { useCallback, useRef } from 'react';

import { useStaffStore } from '../stores';
import { Staff } from '../types';
import { staffsService } from '../services/staffs.service';

import { FunctionName } from '@/lib/enums';

// API call to fetch staffs by role and department
const fetchStaffs = async (
  roleKey: string,
  departmentId: string,
  functionName: FunctionName
): Promise<Staff[]> => {
  const response = await staffsService.getStaffs({
    role_key: roleKey,
    department_id: departmentId,
    function_name: functionName,
  });
  if (!response) {
    throw new Error('Failed to fetch staffs');
  }
  // API response structure: { data: [...], timestamp, requestId } or direct array
  return Array.isArray(response) ? response : response || [];
};

export const useStaffs = () => {
  const {
    staffs,
    staffsByDepartment,
    loading,
    error,
    setStaffs,
    setStaffsByDepartment,
    getStaffsByDepartment,
    setLoading,
    setError,
    getStaffById,
    getStaffByCode,
    clearStaffs,
    clearStaffsByDepartment,
  } = useStaffStore();

  const loadStaffs = useCallback(
    async (roleKey: string, departmentId: string, functionName: FunctionName) => {
      try {
        setLoading(true);
        const data = await fetchStaffs(roleKey, departmentId, functionName);
        setStaffs(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load staffs');
      } finally {
        setLoading(false);
      }
    },
    [setStaffs, setLoading, setError]
  );

  // Use refs to avoid dependency issues
  const setStaffsByDepartmentRef = useRef(setStaffsByDepartment);
  const setLoadingRef = useRef(setLoading);
  const setErrorRef = useRef(setError);
  const inFlightRequestsRef = useRef<Map<string, Promise<Staff[]>>>(new Map());

  // Update refs when functions change
  setStaffsByDepartmentRef.current = setStaffsByDepartment;
  setLoadingRef.current = setLoading;
  setErrorRef.current = setError;

  const loadStaffsByDepartment = useCallback(
    async (roleKey: string, departmentId: string, functionName: FunctionName) => {
      const requestKey = `${functionName}:${departmentId}:${roleKey}`;

      try {
        setLoadingRef.current(true);

        // Always revalidate to keep staff option lists in sync after permission changes.
        let request = inFlightRequestsRef.current.get(requestKey);
        if (!request) {
          request = fetchStaffs(roleKey, departmentId, functionName);
          inFlightRequestsRef.current.set(requestKey, request);
        }

        const data = await request;
        setStaffsByDepartmentRef.current(departmentId, data, roleKey);
      } catch (err) {
        console.error(`Error loading staffs for department ${departmentId}:`, err);
        setErrorRef.current(err instanceof Error ? err.message : 'Failed to load staffs');
      } finally {
        inFlightRequestsRef.current.delete(requestKey);
        setLoadingRef.current(false);
      }
    },
    []
  ); // Empty dependencies array

  return {
    staffs,
    staffsByDepartment,
    loading,
    error,
    loadStaffs,
    loadStaffsByDepartment,
    getStaffsByDepartment,
    getStaffById,
    getStaffByCode,
    clearStaffs,
    clearStaffsByDepartment,
  };
};
